import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Alert, Button, Snackbar, Tooltip} from "@mui/material";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { api } from "../../redux/api";
import { useEffect, useRef, useState } from "react";
import SaveConfirmationDialog from "./SaveConfirmationDialog";
import { addAdditionalTemplateHeaderDetails, rcTreeToInboundTemplate, rcTreeToOutboundTemplate, rcTreeToOutboundXML, rcTreeToWorkflowTemplate } from "../../utils/rctreeDataConversion";
import { isEqual } from "lodash";

export default function SaveButton() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const treeData = useSelector((state) => state.treeData);
  const { account } = useSelector((state) => state.queryParams);
  const [alertOpen, setAlertOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [unsavedList, setUnsavedList] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [temporaryTemplateRow, setTemporaryTemplateRow] = useState({});
  const [selectedTemplate, setSelectedTemplate] = useState(false);
  const [checkedTemplates, setCheckedTemplates] = useState([]);
  const [reloadPopup, setReloadPopup] = useState(false);
  const isSaveClicked = useRef(false);
  const isInputFocused = useSelector((state) => state.treeData.isInputFocused);
  const handleAlertToggle = (open) => setAlertOpen(open);

  const handleSaveClick = () => {
    if (isInputFocused) {
      isSaveClicked.current = true;
      return;
    }

    const ibWfg = treeData.inbound.leftTree.tree.body.selectedNode?.workflowGroup || false;
    const obWfg = treeData.outbound.leftTree.tree.body.selectedNode?.workflowGroup || false;
    const wfWfg = treeData.workflow.leftTree.tree.body.selectedNode?.workflowGroup || false;

    const ibName = treeData.inbound.leftTree.tree.body.selectedNode?.title || false;
    const obName = treeData.outbound.leftTree.tree.body.selectedNode?.title || false;
    const wfName = treeData.workflow.leftTree.tree.body.selectedNode?.title || false;

    const ibPath = account && ibWfg && ibName ? `${account}/${ibWfg}/${ibName}` : false;
    const obPath = account && obWfg && obName ? `${account}/${obWfg}/${obName}` : false;
    const wfPath = account && wfWfg && wfName ? `${account}/${wfWfg}/${wfName}` : false;

    const currWfg = treeData[treeData.currentScreen].leftTree.tree.body.selectedNode?.workflowGroup || false;
    const currName = treeData[treeData.currentScreen].leftTree.tree.body.selectedNode?.title || false;
    const currentTemplatePath = account && currWfg && currName ? `${account}/${currWfg}/${currName}` : false;

    let diff = false;
    let ibDiff = false;
    let obDiff = false;
    let wfDiff = false;

    setDialogOpen(true);
    setListLoading(true);

    //20 sec timeout on loading state, incase failed response or user loses connection.
    setTimeout(() => {
      if (listLoading) setListLoading(false);
    }, 20000);

    //process response data and set variables that are used in SaveConfirmationDialog template list.
    const setTemplateList = (resData) => {
      //flatten obj to array, map each unsaved list, add type and path for identification in table component.
      let processedUnsavedTemplates = resData?.current_state === "SUCCESS" && resData?.data ? [
        ...resData.data.inbound_templates.map(o => {
          return {
            ...o,
            template_type: 'inbound',
            path: `${o.account}/${o.workflow_group}/${o.template}`
          }
        }),
        ...resData.data.outbound_templates.map(o => {
          return {
            ...o,
            template_type: 'outbound',
            path: `${o.account}/${o.workflow_group}/${o.template}`
          }
        }),
        ...resData.data.workflow.map(o => {
          return {
            ...o,
            template_type: 'workflow',
            path: `${o.account}/${o.workflow_group}/${o.template}`
          }
        })
      ] : [];

      //set vars used in SaveConfirmationDialog template list
      //if current active template is not found in unsaved list, set selected template to first in list.
      const setVars = () => {
        setUnsavedList(processedUnsavedTemplates);
        setSelectedTemplate(
          currentTemplatePath && processedUnsavedTemplates.map(o => o.path).includes(currentTemplatePath) ? currentTemplatePath 
          : processedUnsavedTemplates.length ? processedUnsavedTemplates[0].path : false
        );
        setCheckedTemplates(processedUnsavedTemplates.map(o => o.path));
        setListLoading(false);
      }

      //if template diff and if one of the ib/ob/wf templates are not found in unsaved list, 
      // validate template data for each, then append template(s) to beginning.
      //else just set vars with unsaved list
      if (processedUnsavedTemplates.length > 0) {
        //validate inbound, then outbound, then workflow.

        function validateInboundTemplate() {
          if (ibDiff && !processedUnsavedTemplates.filter(o => o.path === ibPath).length) {
            dispatch(api.endpoints.validateInboundTemplate.initiate(
              {
                account,
                workflowGroupName: ibWfg,
                templateName: ibName,
                data: rcTreeToInboundTemplate(treeData.inbound.rightTree.tree)
              }, { forceRefetch: true }
            )).then(res => {
              if (res?.data?.current_state === "SUCCESS") {
                processedUnsavedTemplates.unshift({
                  account: account,
                  workflow_group: ibWfg,
                  template: ibName,
                  warnings: res.data?.data || [],
                  template_type: "inbound",
                  path: ibPath
                });
              }
              validateOutboundTemplate();
            })
          } else {
            validateOutboundTemplate();
          }
        }

        function validateOutboundTemplate() {
          if (obDiff && !processedUnsavedTemplates.filter(o => o.path === obPath).length) {
            dispatch(api.endpoints.validateOutboundTemplate.initiate(
              {
                account,
                workflowGroupName: obWfg,
                templateName: obName,
                data: treeData.outbound.rightTree.tree.templateHeader.FILETYPE === "XML" ? rcTreeToOutboundXML(treeData.outbound.rightTree.tree, treeData.outbound.rightTree.initialFetchData) : rcTreeToOutboundTemplate(treeData.outbound.rightTree.tree)
              }, { forceRefetch: true }
            )).then(res => {
              if (res?.data?.current_state === "SUCCESS") {
                processedUnsavedTemplates.unshift({
                  account: account,
                  workflow_group: obWfg,
                  template: obName,
                  warnings: res.data?.data || [],
                  template_type: "outbound",
                  path: obPath
                });          
              }
              validateWorkflowTemplate();
            })
          } else {
            validateWorkflowTemplate();
          }
        }

        function validateWorkflowTemplate() {
          setVars();

          //TODO: not sure if this endpoint is implemented for workflow.
          // if (wfDiff && !processedUnsavedTemplates.filter(o => o.path === wfPath).length) {
          //   dispatch(api.endpoints.validateWorkflowTemplate.initiate(
          //     {
          //       account,
          //       workflowGroupName: wfWfg,
          //       templateName: wfName,
          //       data: rcTreeToWorkflowTemplate(treeData.workflow.rightTree.tree)
          //     }, { forceRefetch: true }
          //   )).then(res => {
          //     if (res.data.current_state === "SUCCESS") {
          //       processedUnsavedTemplates.unshift({
          //         account: account,
          //         workflow_group: wfWfg,
          //         template: wfName,
          //         warnings: res.data?.data || [],
          //         template_type: "workflow",
          //         path: wfPath
          //       });          
          //     }
          //     setVars();
          //   })
          // } else {
          //   setVars();
          // }
        }
        
        //start chain of validation calls
        validateInboundTemplate()
      } else {
        //if no templates to validate, proceed to set vars.
        setVars();
      }
    }

    //send getUnsavedTemplates call, then trigger setTemplateList() with new response data.
    const getUnsavedTemplateList = () => {
      dispatch(api.endpoints.getUnsavedTemplates.initiate(
        { account }, { forceRefetch: true }
      )).then(response => {
        if (response?.data?.current_state === "SUCCESS" && !response.data) {
          console.warn('getUnsavedTemplates no data:', response);
          setTemplateList({
            data: {
              "inbound_templates": [],
              "outbound_templates": [],
              "workflow": [],
              "schedule": []
            }
          });
        }
        else setTemplateList(response.data);
      })
    }

    //Start with inbound, then outbound, then workflow. chain the saves, then fetch unsaved list after last one finishes.

    function checkInboundDiff() {
      //check inbound data for diff, and if true, save unsaved changes to backend
      if (treeData.inbound.templateChanged === true && !['', '/'].includes(treeData.inbound.rightTree.path)) {
        const ibRightTree = treeData.inbound.rightTree;
        let convertedData = rcTreeToInboundTemplate(ibRightTree.tree);

        //if there is a diff in data, save new data to unsaved list before fetching same list from backend.
        if (!isEqual(ibRightTree.initialFetchData, convertedData)) {
          diff = true;
          ibDiff = true;

          const requiredDataForTemplateHeader = {
            ACCOUNT_NAME: account,
            TEMPLATE: ibName,
            WF_GROUP: ibWfg,
          }

          dispatch(api.endpoints.saveUserInboundTemplate.initiate(
            {
              account,
              workflowGroupName: ibWfg,
              templateName: encodeURIComponent(ibName),
              data: addAdditionalTemplateHeaderDetails(convertedData, requiredDataForTemplateHeader,false)
            }, { forceRefetch: true }
          )).then((res) => {
            //save call finished, check for outbound diff
            checkOutboundDiff()
          });
        } else {
          //if no inbound diff, proceed to check for outbound diff
          checkOutboundDiff();
        }
      } else {
        //if no inbound template active in inbound tab, proceed to check for outbound diff
        checkOutboundDiff();
      }
    }

    function checkOutboundDiff() {
      //check outbound data for diff, and if true, save unsaved changes to backend
      if (treeData.outbound.templateChanged === true && !['', '/'].includes(treeData.outbound.rightTree.path)) {
        const obRightTree = treeData.outbound.rightTree;
        let convertedData = treeData.outbound.rightTree.tree.templateHeader.FILETYPE === "XML" ? rcTreeToOutboundXML(treeData.outbound.rightTree.tree, treeData.outbound.rightTree.initialFetchData) : rcTreeToOutboundTemplate(treeData.outbound.rightTree.tree)

        //if there is a diff in data, save new data to unsaved list before fetching same list from backend.
        if (!isEqual(obRightTree.initialFetchData, convertedData)) {
          diff = true;
          obDiff = true;

          const requiredDataForTemplateHeader = {
            ACCOUNT_NAME: account,
            TEMPLATE: obName,
            WF_GROUP: obWfg,
          }

          dispatch(api.endpoints.saveUserOutboundTemplate.initiate(
            {
              account,
              workflowGroupName: obWfg,
              templateName: encodeURIComponent(obName),
              data: addAdditionalTemplateHeaderDetails(convertedData, requiredDataForTemplateHeader,true)
            }, { forceRefetch: true }
          )).then((res) => {
            //save call finished, check for workflow diff
            checkWorkflowDiff();
          })
        } else {
          //if no outbound diff, proceed to check for workflow diff
          checkWorkflowDiff();
        }
      } else {
        //if no outbound template active in outbound tab, proceed to check for workflow diff
        checkWorkflowDiff();
      }
    }
    
    function checkWorkflowDiff() {
      //check workflow data for diff, and if true, save unsaved changes to backend
      if (treeData.workflow.templateChanged === true && !['', '/'].includes(treeData.workflow.rightTree.path)) {
        const wfRightTree = treeData.workflow.rightTree;
        let convertedData = rcTreeToWorkflowTemplate(wfRightTree.tree);

        //if there is a diff in data, save new data to unsaved list before fetching same list from backend.
        if (!isEqual(wfRightTree.initialFetchData, convertedData) || treeData.workflow.templateChanged) {
          diff = true;
          wfDiff = true;

          dispatch(api.endpoints.saveUserWorkflowTemplate.initiate(
            {
              account,
              workflowGroupName: wfWfg,
              templateName: encodeURIComponent(wfName),
              data: convertedData
            }, { forceRefetch: true }
          )).then((res) => {
            //save call finished, fetch unsaved templates list
            getUnsavedTemplateList();
          })
        } else {
          //if no workflow diff, fetch unsaved templates list
          getUnsavedTemplateList();
        }
      } else {
        //if no workflow active in workflow tab, proceed to fetch unsaved templates list
        getUnsavedTemplateList();
      }
    }

    //start the chain of save calls. inbound, then outbound, then workflow.
    checkInboundDiff()
  };

  useEffect(() => {
    const initialDataIB = treeData.inbound.rightTree.initialFetchData;
    const transDataIB = rcTreeToInboundTemplate(treeData.inbound.rightTree.tree);

    const initialDataOB = treeData.outbound.rightTree.initialFetchData;
    const transDataOB = treeData.outbound.rightTree.tree.templateHeader.FILETYPE === "XML" ? rcTreeToOutboundXML(treeData.outbound.rightTree.tree, treeData.outbound.rightTree.initialFetchData) : rcTreeToOutboundTemplate(treeData.outbound.rightTree.tree);

    const initialDataWF = treeData.workflow.rightTree.initialFetchData;
    const transDataWF = rcTreeToWorkflowTemplate(treeData.workflow.rightTree.tree);

    // if there is a diff in data, bind 'beforeunload', else unbind it.
    if (
      (initialDataIB && !isEqual(initialDataIB, transDataIB)) ||
      (initialDataOB && !isEqual(initialDataOB, transDataOB)) ||
      (initialDataWF && !isEqual(initialDataWF, transDataWF))
    ) {
      window.addEventListener('beforeunload', alertUser)
      window.addEventListener('unload', handleTabClosing)
      return () => {
        window.removeEventListener('beforeunload', alertUser)
        window.removeEventListener('unload', handleTabClosing)
      }
    } else {
      window.removeEventListener('beforeunload', alertUser)
      window.removeEventListener('unload', handleTabClosing)
    }
  }, [
    treeData.inbound.rightTree.tree, 
    treeData.inbound.rightTree.initialFetchData,
    treeData.inbound.templateChanged,
    treeData.outbound.rightTree.tree, 
    treeData.outbound.rightTree.initialFetchData,
    treeData.outbound.templateChanged,
    treeData.workflow.rightTree.tree,
    treeData.workflow.rightTree.initialFetchData,
    treeData.workflow.templateChanged,
    treeData.currentScreen
  ]);

  useEffect(() => {
    if(!isInputFocused && isSaveClicked.current) {
      isSaveClicked.current = false;
      handleSaveClick();
    }
  }, [isInputFocused]);

  const handleTabClosing = () => { 
    // IF any task needs to be done after user click reload in Reload Dialog
    setReloadPopup(false);
  }

  const alertUser = (event) => {
    event.preventDefault();
      
    //trigger save button click
    handleSaveClick();
    setReloadPopup(true);
    //custom message is not supported in newer browsers. default to empty string to avoid errors.
    event.returnValue = ''; 
    return ''
  }

  // counter for save button. shows user how many total unsaved templates there are.
  const tdUs = treeData.unsavedTemplates;
  const unsavedCounter = tdUs.inbound_templates.length + tdUs.outbound_templates.length + tdUs.workflow.length;

  return (
    <>
      <Button
        size='small'
        variant="outlined"
        className="margin-right-4"
        onClick={handleSaveClick}
      >
        <SaveOutlinedIcon fontSize="medium" className="margin-right-4" />
        {t("DIAG_SAVE_TIT")}&nbsp;
        {unsavedCounter > 0 && <b>
          ({`${unsavedCounter}`})
        </b>}
        
      </Button>

      {dialogOpen && 
        <SaveConfirmationDialog
          setReloadPopup={setReloadPopup}
          reloadPopup={reloadPopup}
          temporaryTemplateRow={temporaryTemplateRow}
          setTemporaryTemplateRow={setTemporaryTemplateRow}
          unsavedList={unsavedList}
          setUnsavedList={setUnsavedList}
          selectedTemplate={selectedTemplate}
          setSelectedTemplate={setSelectedTemplate}
          checkedTemplates={checkedTemplates}
          setCheckedTemplates={setCheckedTemplates}
          listLoading={listLoading}
          setListLoading={setListLoading}
          // warningObject={warningObject}
          dialogOpen={dialogOpen}
          setDialogOpen={setDialogOpen}
          setAlertOpen={setAlertOpen}
        />
      }
      
      {/* {(isLoading?.inbound || isLoading?.outbound || isLoading?.workflow) &&
        <div className="main-screen-loader"><CircularProgress size={"5rem"} color="primary" /></div>
      }  */}
      <Snackbar open={alertOpen} autoHideDuration={3000} onClose={() => handleAlertToggle(false)}>
        <Alert onClose={() => handleAlertToggle(false)} severity="success" variant="filled" sx={{ width: '100%' }}>
          {t('Templates saved successfully')}
        </Alert>
      </Snackbar>
    </>
  );
}
